// src/portals/fan/pages/LibraryPage/LibraryPage.js
import React, { useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { featuredCreators } from "../../../../data/mockData"; // Ensure this is imported
import PageHeader from "../../components/PageHeader";

// Import all child components
import SubscriptionCard from "./components/SubscriptionCard";
import PurchaseItem from "./components/PurchaseItem";
import ExperienceItem from "./components/ExperienceItem";
import CourseItem from "./components/CourseItem";
import FollowedCreatorCard from "./components/FollowedCreatorCard";
import DonationItem from "./components/DonationItem";
import TipItem from "./components/TipItem";
import { Users, ShoppingBag, UserPlus, Heart, Coffee, Calendar, BookOpen } from "lucide-react";

const LibraryPage = () => {
  const [activeTab, setActiveTab] = useState("subscriptions");
  const { currentUser } = useAuth();

  const { library, donations, tips, following } = currentUser || {};

  const followedCreators = featuredCreators.filter((creator) =>
    following?.includes(creator.username)
  );

  const allTabs = [
    { id: "subscriptions", label: "Subscriptions", hasContent: library?.subscriptions?.length > 0 },
    { id: "purchases", label: "Purchases", hasContent: library?.purchases?.length > 0 },
    { id: "following", label: "Following", hasContent: followedCreators.length > 0 },
    { id: "donations", label: "Donations", hasContent: donations?.length > 0 },
    { id: "tips", label: "Tips", hasContent: tips?.length > 0 },
    { id: "experiences", label: "Experiences", hasContent: library?.experiences?.length > 0 },
    { id: "courses", label: "Courses", hasContent: library?.courses?.length > 0 },
  ];

  const tabs = allTabs.filter(tab => tab.hasContent);

  // Default to first available tab if active tab is empty
  React.useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const [searchTerm, setSearchTerm] = useState("");

  // Guard clause for when user data is not yet loaded
  if (!currentUser) {
    return (
      <div className="flex flex-col h-full bg-slate-50">
        <PageHeader title="My Collection" />
        <p className="p-4 text-center text-slate-500">
          Loading your collection...
        </p>
      </div>
    );
  }

  const renderContent = () => {
    // Helper to filter items based on search term
    const filterItems = (items) => {
      if (!items) return [];
      if (!searchTerm) return items;
      const lowerTerm = searchTerm.toLowerCase();
      return items.filter(item => {
        const text = item.title || item.name || item.creatorName || "";
        return String(text).toLowerCase().includes(lowerTerm);
      });
    };

    const EmptyState = ({ icon: Icon, message }) => (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Icon size={48} className="mb-4 opacity-50" />
        <p>{message}</p>
      </div>
    );

    switch (activeTab) {
      case "subscriptions":
        const filteredSubs = filterItems(library?.subscriptions);
        if (filteredSubs.length === 0) return <EmptyState icon={Users} message="No subscriptions found." />;
        return (
          <div className="p-4 space-y-4">
            {filteredSubs.map((sub) => (
              <SubscriptionCard key={sub.id} subscription={sub} />
            ))}
          </div>
        );
      case "purchases":
        const filteredPurchases = filterItems(library?.purchases);
        if (filteredPurchases.length === 0) return <EmptyState icon={ShoppingBag} message="No purchases found." />;
        return (
          <div className="p-4 space-y-3">
            {filteredPurchases.map((item) => (
              <PurchaseItem key={item.id} purchase={item} />
            ))}
          </div>
        );
      case "following":
        const filteredFollowing = followedCreators.filter(c => 
          (c.displayName || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
          (c.username || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filteredFollowing.length === 0) return <EmptyState icon={UserPlus} message="No creators found." />;
        return (
          <div className="p-4 space-y-3">
            {filteredFollowing.map((creator) => (
              <FollowedCreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        );
      case "donations":
        const filteredDonations = filterItems(donations);
        if (filteredDonations.length === 0) return <EmptyState icon={Heart} message="No donations found." />;
        return (
          <div className="p-4">
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {filteredDonations.map((donation) => (
                <DonationItem key={donation.id} donation={donation} />
              ))}
            </div>
          </div>
        );
      case "tips":
        const filteredTips = filterItems(tips);
        if (filteredTips.length === 0) return <EmptyState icon={Coffee} message="No tips found." />;
        return (
          <div className="p-4">
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {filteredTips.map((tip) => (
                <TipItem key={tip.id} tip={tip} />
              ))}
            </div>
          </div>
        );
      case "experiences":
        const filteredExperiences = filterItems(library?.experiences);
        if (filteredExperiences.length === 0) return <EmptyState icon={Calendar} message="No experiences found." />;
        return (
          <div className="p-4 space-y-3">
            {filteredExperiences.map((exp) => (
              <ExperienceItem key={exp.id} experience={exp} />
            ))}
          </div>
        );
      case "courses":
        const filteredCourses = filterItems(library?.courses);
        if (filteredCourses.length === 0) return <EmptyState icon={BookOpen} message="No courses found." />;
        return (
          <div className="p-4 space-y-4">
            {filteredCourses.map((course) => (
              <CourseItem key={course.id} course={course} />
            ))}
          </div>
        );
      default:
        return (
          <p className="p-4 text-center text-slate-500">No items to display.</p>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <PageHeader title="My Collection" />
      <div className="bg-white px-4 py-2 border-b border-slate-100">
        <input
          type="text"
          placeholder="Filter collection..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 bg-slate-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div className="border-b border-slate-200 bg-white">
        {/* ✨ CSS FIX: These classes enable horizontal scrolling */}
        <nav className="flex overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-primary text-primary"
                  : "border-b-2 border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="flex-grow overflow-y-auto">{renderContent()}</div>
    </div>
  );
};

export default LibraryPage;
